/**
 * CMS Channels Query
 *
 * Server-side queries for fetching channel data with streaming and contact info.
 */

import { query } from "@solidjs/router";
import type { CMSChannelResponse } from "~/types/cms";
import { env } from "~/config/env";
import { logger } from "~/lib/logger/logger";
import { fetchFromCMS, filterByTenant, findByTenant } from "../queryUtils";
import {
  buildChannelsListUrl,
  buildChannelByIdentifierUrl,
  buildStreamingChannelsListUrl,
} from "../queryUrls";

/**
 * Server-side query for fetching all channels
 *
 * @param tenantSlug - The tenant slug (e.g., 'absolut-radio')
 * @returns Array of channels with full configuration, or empty array if error
 */
export const getChannelsByTenantQuery = query(async (tenantSlug: string) => {
  "use server";

  const LOG_PREFIX = "[channelsByTenant]";

  // URL built via shared queryUrls module (also used by performance audit)
  const url = `${env.cmsApiUrl}${buildChannelsListUrl()}`;

  const data = await fetchFromCMS<CMSChannelResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs || !Array.isArray(data.docs)) {
    logger.warn(`${LOG_PREFIX} Invalid response structure`);
    return [];
  }

  logger.info(`${LOG_PREFIX} Received ${data.docs.length} channels`);

  // Filter by tenant (Payload doesn't support querying nested relationship fields)
  const channels = filterByTenant(data.docs, tenantSlug);

  if (channels.length === 0) {
    logger.warn(`${LOG_PREFIX} No channels found for tenant: ${tenantSlug}`);
  } else {
    logger.info(`${LOG_PREFIX} Found ${channels.length} channels`);
  }

  return channels;
}, "channelsByTenant");

/**
 * Server-side query for fetching single channel by identifier
 *
 * @param identifier - The channel identifier (e.g., 'hot', 'relax')
 * @param tenantSlug - The tenant slug (e.g., 'absolut-radio')
 * @returns Channel with full configuration or null if not found
 */
export const getChannelByIdentifierQuery = query(async (identifier: string, tenantSlug: string) => {
  "use server";

  const LOG_PREFIX = "[channelByIdentifier]";

  // URL built via shared queryUrls module (also used by performance audit)
  const url = `${env.cmsApiUrl}${buildChannelByIdentifierUrl({ identifier, tenantSlug })}`;

  const data = await fetchFromCMS<CMSChannelResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs || data.docs.length === 0) {
    logger.warn(`${LOG_PREFIX} No channel found for identifier: ${identifier}`);
    return null;
  }

  // Payload ignores nested tenant[slug] filters — filter client-side
  const channel = findByTenant(data.docs, tenantSlug);

  if (!channel) {
    logger.warn(`${LOG_PREFIX} No channel found for tenant: ${tenantSlug}`);
    return null;
  }

  logger.info(`${LOG_PREFIX} Found channel: ${channel.displayName}`);
  return channel;
}, "channelByIdentifier");

/**
 * Server-side query for fetching streaming-enabled channels only
 *
 * @param tenantSlug - The tenant slug (e.g., 'absolut-radio')
 * @returns Array of channels that have live streaming enabled
 */
export const getStreamingChannelsByTenantQuery = query(async (tenantSlug: string) => {
  "use server";

  const LOG_PREFIX = "[streamingChannelsByTenant]";

  // URL built via shared queryUrls module (also used by performance audit)
  const url = `${env.cmsApiUrl}${buildStreamingChannelsListUrl()}`;

  const data = await fetchFromCMS<CMSChannelResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs || !Array.isArray(data.docs)) {
    logger.warn(`${LOG_PREFIX} Invalid response structure`);
    return [];
  }

  logger.info(`${LOG_PREFIX} Received ${data.docs.length} channels`);

  // Filter by tenant
  const channels = filterByTenant(data.docs, tenantSlug);

  if (channels.length === 0) {
    logger.warn(`${LOG_PREFIX} No streaming channels found for tenant: ${tenantSlug}`);
  } else {
    logger.info(`${LOG_PREFIX} Found ${channels.length} streaming channels`);
  }

  return channels;
}, "streamingChannelsByTenant");
